import moment from "moment";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import {
  texto,
  numero,
  moneda,
  centro,
  construirHojaTabla,
  limpiarNombreArchivo,
  paraPdf,
  descargarLibro,
} from "../../../../Components/ExcelTablaReporte";
import reporteFiscalPdf from "../../../../Pdf/reporteFiscalPdf";

pdfMake.vfs = pdfFonts.pdfMake.vfs;

/**
 * Excel y PDF del Reporte Insumos Y Equivalencias.
 *
 * Una sola tabla, armada una vez, que alimenta los dos archivos para que no
 * puedan decir cosas distintas (mismo patron que
 * `ReporteCostoVsUltimaCompra/exportarCostoVsUltimaCompra.js`).
 *
 * Aqui las columnas NO son constantes: despues de `Costo` va una columna por
 * cada lista de precios activa de la empresa, que el backend devuelve en
 * `listasPrecios`.
 */

/** Insumo en turquesa Biwo, equivalencia en ambar, en dos tonos que alternan por insumo. */
const RELLENO_PDF = {
  insumoA: "#BFEFF5",
  insumoB: "#DFF7FA",
  equivalenciaA: "#FFF1D6",
  equivalenciaB: "#FFF9EC",
};

export const resalteDe = (registro) =>
  registro.tipo === "insumo"
    ? registro.bloque
      ? "insumoB"
      : "insumoA"
    : registro.bloque
      ? "equivalenciaB"
      : "equivalenciaA";

/**
 * Las columnas fijas mas una por lista de precios.
 *
 * Solo las dos primeras listas entran al PDF: en LETTER horizontal ya van 6
 * columnas fijas y una tercera lista deja los textos ilegibles. El Excel si
 * las trae todas.
 */
export const construirColumnas = (listasPrecios = []) => [
  { titulo: "Tipo", tipo: "texto", ancho: 14, anchoPdf: 54 },
  // `Insumo.codigo` es Number: como celda numerica de verdad, Excel lo ordena
  // 1, 2, 10 y no 1, 10, 2.
  { titulo: "Codigo", tipo: "numero", ancho: 10, anchoPdf: 38 },
  { titulo: "Clave", tipo: "texto", ancho: 14, enPdf: false },
  { titulo: "Descripcion", tipo: "texto", ancho: 44, anchoPdf: 150 },
  { titulo: "Familia", tipo: "texto", ancho: 20, enPdf: false },
  { titulo: "Marca", tipo: "texto", ancho: 18, enPdf: false },
  { titulo: "Unidad", tipo: "texto", ancho: 12, anchoPdf: 52 },
  { titulo: "Factor", tipo: "numero", ancho: 9, anchoPdf: 40 },
  { titulo: "Costo", tipo: "moneda", ancho: 14, anchoPdf: 66 },
  ...listasPrecios.map((lista, indice) => ({
    titulo: lista.nombre,
    tipo: "moneda",
    ancho: 15,
    anchoPdf: 66,
    enPdf: indice < 2,
  })),
  { titulo: "Venta", tipo: "texto", ancho: 8, enPdf: false },
  { titulo: "Venta Online", tipo: "texto", ancho: 12, enPdf: false },
  { titulo: "Inventariable", tipo: "texto", ancho: 12, enPdf: false },
  { titulo: "Activo", tipo: "texto", ancho: 8, enPdf: false },
];

/** Un booleano que solo aplica al insumo padre: en la equivalencia va vacio. */
const siNo = (valor) => centro(valor === null || valor === undefined ? "" : valor ? "Si" : "No");

const ETIQUETA_TIPO = { insumo: "INSUMO", equivalencia: "EQUIVALENCIA" };

export const construirTabla = (registros, listasPrecios = []) => {
  const columnas = construirColumnas(listasPrecios);

  const filas = (registros || []).map((registro) => {
    const resalte = resalteDe(registro);
    // El Excel lee `resalte` (solo aporta fill y font, para no romper el numFmt
    // de Contabilidad) y el PDF lee `fillColor`, que le gana a su zebra por
    // indice (`reporteFiscalPdf`: `celda.fillColor || fondo`).
    const pintar = (celda) => ({ ...celda, resalte, fillColor: RELLENO_PDF[resalte] });

    return [
      texto(ETIQUETA_TIPO[registro.tipo] || ""),
      numero(registro.codigo),
      texto(registro.clave),
      texto(registro.descripcion),
      texto(registro.familia),
      texto(registro.marca),
      texto(registro.unidad),
      // Solo el numero: los rotulos de la pantalla de captura describen el
      // factor al reves que el codigo, asi que una frase tipo "1 CAJA = 12 PZA"
      // puede quedar invertida. El factor vive en la equivalencia, no en el insumo.
      numero(registro.factor),
      moneda(registro.costo),
      ...listasPrecios.map((lista) => moneda((registro.precios || {})[lista.precioId])),
      siNo(registro.venta),
      siNo(registro.ventaOnline),
      siNo(registro.inventariable),
      siNo(registro.activo),
    ].map(pintar);
  });

  // Sin fila de totales: todo el dinero de este reporte es unitario (costo y
  // precio por unidad), y sumar costos unitarios de insumos distintos no
  // significa nada. Con los totales en null, `construirHojaTabla` omite la fila.
  return { columnas, filas, totales: columnas.map(() => null) };
};

const TITULO = "Catalogo de insumos con sus equivalencias";

const nombreArchivo = (empresaNombre) =>
  `Insumos_Y_Equivalencias_${limpiarNombreArchivo(empresaNombre)}_${moment().format("YYYY-MM-DD")}`;

const indicadoresDe = (resumen = {}) => [
  { etiqueta: "Insumos", valor: String(resumen.insumos || 0) },
  { etiqueta: "Con equivalencias", valor: String(resumen.insumosConEquivalencias || 0) },
  { etiqueta: "Equivalencias", valor: String(resumen.equivalencias || 0) },
  { etiqueta: "Renglones", valor: String(resumen.renglones || 0) },
];

export const descargarExcel = ({ datos, listasPrecios, empresaNombre, descripcionFiltros }) => {
  descargarLibro(
    [
      {
        nombre: "Insumos Y Equivalencias",
        hoja: construirHojaTabla(construirTabla(datos, listasPrecios), {
          titulo: TITULO,
          empresaNombre,
          descripcionFiltros,
        }),
      },
    ],
    nombreArchivo(empresaNombre)
  );
};

export const descargarPdf = ({
  datos,
  listasPrecios,
  resumen,
  empresaNombre,
  descripcionFiltros,
}) => {
  const tabla = construirTabla(datos, listasPrecios);

  pdfMake
    .createPdf(
      reporteFiscalPdf({
        titulo: "Reporte Insumos Y Equivalencias",
        empresaNombre,
        descripcionFiltros,
        indicadores: indicadoresDe(resumen),
        contador: tabla.filas.length,
        secciones: [{ titulo: TITULO, ...paraPdf(tabla) }],
      })
    )
    .download(`${nombreArchivo(empresaNombre)}.pdf`);
};
